import { ServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/ServerSideDataGridModel.tsx";
import {
	ObjectChangeLogDataGridApi,
	ObjectChangeLogDataGridApi_ColumnDefinitionDto,
	ObjectChangeLogDataGridApi_ObjectModificationEventDto,
} from "src/api/generated/changeLogging/objectChangeLogDataGridApi.ts";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { dateTimeColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { ObjectModificationLogEventLine } from "src/api/generated/erp/db/types/tables/objectModificationLogEventLine.ts";
import { LabelValueView } from "src/components/common/misc/LabelValueView.tsx";
import { AavoSelectField } from "../../common/inputFields/AavoSelectField.tsx";
import { genericNullableValue } from "src/utils/genericNullableValue.ts";
import { AavoObjectRef } from "src/api/generated/common/aavoObject/aavoObjectRef.ts";
import { localizeUnsafe } from "src/utils/localizationUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ObjectChangeLogDataGridProps {
	objectRef: AavoObjectRef;
}

export const ObjectChangeLogDataGrid = ({ objectRef }: ObjectChangeLogDataGridProps) => {
	return (
		<ServerSideDataGridModel
			gridId={"E86626432E6D505C4"}
			fetchData={(params) =>
				ObjectChangeLogDataGridApi.getObjectChangeLog({
					objectId: objectRef.objectId,
					objectType: objectRef.objectType,
					...params,
				})
			}
			initialParams={{
				columnNameFilter: genericNullableValue<string>(""),
			}}
			getDataModelResult={(data) => data.gridData}
			getRowId={(row) => row.objectModificationLogEventId}
			render={({ data: { columns }, dataGridProps: { initialState, ...otherDataGridProps }, refreshData }) => {
				const allRowIds = otherDataGridProps.rows.map((event) => otherDataGridProps.getRowId(event));
				return (
					<AavoDataGrid<ObjectChangeLogDataGridApi_ObjectModificationEventDto>
						initialState={{
							...initialState,
							detailPanel: {
								expandedRowIds: new Set(allRowIds),
							},
						}}
						columns={[
							dateTimeColumn({
								field: "modifiedAt",
								headerName: i18n.t("modified_at"),
								width: 200,
							}),
							textColumn({
								field: "modifiedByUserName",
								headerName: i18n.t("modified_by"),
								width: 200,
							}),
						]}
						getDetailPanelContent={({ row }) => (
							<EventLinesContent lines={row.modificationEventLines} columns={columns} />
						)}
						getDetailPanelHeight={() => "auto"}
						actionBarComponents={
							<>
								<AavoSelectField
									label={i18n.t("changed_field")}
									options={columns}
									getOptionKey={(option) => option.columnName}
									getOptionLabel={(option) => localizeUnsafe(option.columnLabelLocalizationKey)}
									onChange={async (value) => await refreshData({ columnNameFilter: value })}
								/>
							</>
						}
						agentContext={{
							key: defineAgentUiContextKey("changeLogging.objectChangeLog.grid"),
							data: {
								objectType: objectRef.objectType,
								objectId: objectRef.objectId ?? null,
							},
							rowFields: ["objectModificationLogEventId", "modifiedAt", "modifiedByUserId"],
							selectedRowPathSegment: (row) =>
								`${i18n.t("change_log")} ${row.objectModificationLogEventId}`,
						}}
						{...otherDataGridProps}
					/>
				);
			}}
		/>
	);
};

interface EventLinesContentProps {
	lines: ObjectModificationLogEventLine[];
	columns: ObjectChangeLogDataGridApi_ColumnDefinitionDto[];
}

const EventLinesContent = ({ lines, columns }: EventLinesContentProps) => {
	const columnsByName: Record<string, ObjectChangeLogDataGridApi_ColumnDefinitionDto> = columns.reduce(
		(acc, column) => ({
			...acc,
			[column.columnName]: column,
		}),
		{},
	);
	return (
		<LabelValueView
			sx={{
				paddingLeft: "3.75rem",
				borderBottom: "1px solid grey",
			}}
			fontSize={"small"}
			items={lines.map((line) => {
				const column = columnsByName[line.columnName];
				const labelText = column == null ? line.columnName : localizeUnsafe(column.columnLabelLocalizationKey);
				const arrow = "\u21FE";
				const oldValue = formatLogValue(line.oldValue, column?.currencyCode);
				const newValue = formatLogValue(line.newValue, column?.currencyCode);
				return {
					label: labelText,
					value: `${oldValue} ${arrow} ${newValue}`,
				};
			})}
		/>
	);
};

function formatLogValue(value: string | null | undefined, currencyCode: string | null | undefined): string {
	if (value == null) return "";
	if (currencyCode == null || value === "<tyhjä>") return value;
	return `${value} ${currencyCode}`;
}
